import { useEffect, useRef } from "react";
import Reveal from "@Components/Reveal/Reveal";
import styles from "./Steps.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * The three stages.
 *
 * ⚠️  THE COPY IS THE CURRENT /ambassador PAGE'S, VERBATIM — so this section
 * renders the SAME `ambassador.steps.*` keys rather than a v2 copy of them. Both
 * pages are on the `ambassador` route tag, so the keys are already loaded, and a
 * copy change reaches both at once.
 */
const STEPS = [
  { title: "ambassador.steps.enroll.title", description: "ambassador.steps.enroll.subtitle" },
  { title: "ambassador.steps.engage.title", description: "ambassador.steps.engage.subtitle" },
  { title: "ambassador.steps.expand.title", description: "ambassador.steps.expand.subtitle" },
];

// The fill runs 0 → 1 as the rail travels between these two points down the
// viewport, expressed as a fraction of viewport height.
//
// The span between them is the whole animation's scroll budget: 1.05 → 0.2 is
// 0.85 of a viewport, so the progression reads at a normal scrolling pace. An
// earlier 0.85 → 0.35 gave it only half a viewport and the three steps lit in a
// flick. Starting just past 1.0 means it begins the moment the rail appears from
// below, and it still completes on step 3 well before the section leaves — no
// pinning, the reader keeps control of the scroll.
const START = 1.05;
const END = 0.2;

/** Sums an element's layout offset left up to an ancestor. */
const offsetLeftWithin = (el: HTMLElement, ancestor: HTMLElement): number => {
  let x = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    x += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }
  return x;
};

const offsetTopWithin = (el: HTMLElement, ancestor: HTMLElement): number => {
  let y = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return y;
};

/**
 * Below this width the rail is VERTICAL — a line down the left with the chips on
 * it, matching the Group Booking stepper — so the progress runs top-to-bottom and
 * the chips are reached in order of their `offsetTop`, not their `offsetLeft`.
 *
 * Kept in sync with `@include below(md)` in the stylesheet by hand; there is no
 * way to read a Sass breakpoint from here. 899.98 is what `below(md)` compiles to.
 */
const VERTICAL_RAIL = "(max-width: 899.98px)";

/**
 * How to join — three steps on a horizontal timeline whose rule fills as you
 * scroll, lighting each numbered chip and playing its mark as the fill passes.
 *
 * The timeline is Scholarship's, copied rather than shared — see the note at the
 * top of the stylesheet for why. What is new here is the animated mark above
 * each chip.
 *
 * Nothing is pinned: the progress is mapped to the section's own travel down the
 * viewport, so it finishes on step 3 and the page keeps scrolling normally.
 *
 * Degrades to a plain grey rule without JS, and shows the timeline complete for
 * reduced-motion users.
 */
const Steps = () => {
  const { t } = useTranslationHook();
  const railRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  /** Which mark should be playing: one the fill has just reached, or a hovered column. */

  useEffect(() => {
    const rail = railRef.current;
    const fill = fillRef.current;
    if (!rail || !fill) return undefined;

    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let chips: HTMLElement[] = [];
    let centres: number[] = [];
    // The rail's extent along whichever axis it currently runs.
    let railSize = 0;
    let lit = -1;

    const measure = () => {
      chips = Array.from(rail.querySelectorAll<HTMLElement>("[data-step-chip]"));
      const vertical = window.matchMedia(VERTICAL_RAIL).matches;
      railSize = vertical ? rail.clientHeight : rail.clientWidth;
      // offsetLeft/offsetTop rather than rects: layout-based, so unaffected by the
      // Reveal transform on the heading block or any in-flight animation.
      centres = chips.map((c) =>
        vertical
          ? offsetTopWithin(c, rail) + c.offsetHeight / 2
          : offsetLeftWithin(c, rail) + c.offsetWidth / 2,
      );
    };

    const paint = (count: number) => {
      if (count === lit) return;
      lit = count;
      chips.forEach((chip, i) => {
        chip.classList.toggle(styles.chipActive, i < count);
      });
    };

    const update = () => {
      if (!railSize) return;
      const { top } = rail.getBoundingClientRect();
      const start = window.innerHeight * START;
      const end = window.innerHeight * END;
      const progress = Math.min(1, Math.max(0, (start - top) / (start - end)));

      // A NUMBER, not a transform. The stylesheet decides the axis — scaleX while
      // the rail is horizontal, scaleY once it is vertical — so the two can never
      // disagree about which way the fill grows.
      fill.style.setProperty("--progress", progress.toFixed(4));

      // Step 1's chip sits at the rail's very start, so it needs progress to have
      // actually begun before it counts as reached.
      const edge = progress * railSize;
      paint(progress > 0 ? centres.filter((c) => c <= edge + 0.5).length : 0);
    };

    measure();

    if (reduced) {
      fill.style.setProperty("--progress", "1");
      paint(chips.length);
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    let raf = 0;
    const onScroll = () => {
      if (!raf) {
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          update();
        });
      }
    };
    const onResize = () => {
      measure();
      update();
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {/* ⚠️  HEADING AND LEDE, NOT A TWO-TONE HEADING. The subtitle used to be a
            second line INSIDE the h2, styled only with a grey colour — so it
            inherited `section-title` and set a 52-character sentence at 40px,
            which dwarfed the title above it.
            This is the pattern every other section on this page already uses
            (see Benefits): `section-title` for the heading, `section-lede` at
            16px for the supporting line. Steps was the only outlier. */}
        {/* `inview`, not the default `early`: a section heading is scrolled to
            deliberately, and the default trigger fires a fifth of a screen below
            the fold — so its 0.7s entrance was over before the heading arrived
            and read as no animation at all. The cards and list items below keep
            the default, where a reader should never wait on a transition. */}
        <Reveal className={styles.head} trigger="inview">
          <h2 className={styles.heading}>{t("ambassador.steps.title")}</h2>
          <p className={styles.lede}>{t("ambassador.steps.subtitle")}</p>
        </Reveal>

        <div ref={railRef} className={styles.rail}>
          {/* Track, with the dark progress fill scaling out of its left edge. */}
          <span className={styles.rule} aria-hidden="true">
            <span ref={fillRef} className={styles.ruleFill} />
          </span>

          <div className={styles.cols}>
            {STEPS.map((step, i) => (
              <div key={step.title} className={styles.col}>
                {/* ⚠️  NO ICON HERE. Each step used to carry an animated mark that
                    played as the rule's fill reached its chip. It was removed on
                    request, and with it went the `activeMark` state, the
                    `onMouseEnter` replay, the three Lottie JSONs and the
                    `.body` / `.copy` wrappers that positioned it — the chip
                    lighting is driven separately by the scroll handler through
                    `data-step-chip`, so none of that was load-bearing. */}
                <span className={styles.chip} data-step-chip>
                  {i + 1}
                </span>
                <span className={styles.colTitle}>{t(step.title)}</span>
                <span className={styles.colText}>{t(step.description)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default wrapperHOC(Steps, {
  componentName: "Steps-AmbassadorV2",
  showForChina: true,
});
